// The data layer, assembled: the outbox writes, the syncer sends, the hub
// reads. Every dependency with a clock, a network or a browser API is
// injected, so a test runs the whole layer on fake-indexeddb and fake time.

import { questionnaireFor as libQuestionnaireFor } from "poweryourflow-lib";
import type { FieldValues } from "poweryourflow-lib";
import { DB_NAME, PROFILES, deleteDatabase, inTransaction, openDatabase } from "./db";
import { holdsNothing, readHub, refreshHub } from "./hub";
import * as outbox from "./outbox";
import { ANSWER_WRITE_BUDGET_MS, FIRST_HUB_WAIT_MS, LOGOUT_SYNC_TIMEOUT_MS } from "./policy";
import { Syncer, errorCode } from "./syncer";
import type { PassResult } from "./syncer";
import type {
  AnswerValue,
  AssessmentId,
  AssessmentRun,
  HubState,
  OnlineSource,
  Profile,
  ProfileFields,
  QuestionnaireLookup,
  SyncLock,
  Timers,
  Transport,
} from "./types";

export interface DataLayerOptions {
  transport: Transport;
  questionnaireFor?: QuestionnaireLookup;
  indexedDB?: IDBFactory;
  dbName?: string;
  timers?: Timers;
  online?: OnlineSource;
  lock?: SyncLock;
  now?: () => number;
  /** Runs per sync pass; OUTBOX_BATCH by default and at most. */
  batch?: number;
}

export interface DataLayer {
  /**
   * Stores one screen's answer on the device, with the fields drawn under
   * its options (SPEC-21), and returns the run; the sync happens after, in
   * the background.
   */
  recordAnswer(
    accountId: string,
    assessmentId: AssessmentId,
    questionId: string,
    value: AnswerValue,
    fields?: FieldValues,
  ): Promise<AssessmentRun>;
  /** "Terminar": throws when the lib says a required question has no answer. */
  recordFinish(accountId: string, assessmentId: AssessmentId): Promise<AssessmentRun>;
  /** "Refazer" on a finished assessment: the next version, empty. */
  requestRedo(accountId: string, assessmentId: AssessmentId): Promise<AssessmentRun>;
  /**
   * Sends Dados gerais to the server and keeps the stored profile on the
   * device. Online only: it rejects when the server cannot be reached, and
   * the screen says so (SPEC-14 sends the first name at sign-up, when the
   * network is there by definition).
   */
  saveProfile(fields: ProfileFields): Promise<Profile>;
  /**
   * The local hub at once; `onRefresh` gets the server's when it arrives,
   * and the hub again once a Dados gerais save still in flight is stored.
   * Offline, it is never called. A device that holds nothing of the account
   * waits for the server first, FIRST_HUB_WAIT_MS at most.
   */
  loadHub(accountId: string, onRefresh: (state: HubState) => void): Promise<HubState>;
  /** Runs of the account with changes the server has not confirmed. */
  pendingAnswers(accountId: string): Promise<number>;
  /** One last sync pass, LOGOUT_SYNC_TIMEOUT_MS at most; resolves with the runs still unsent (SPEC-17's warning). */
  prepareLogout(accountId: string): Promise<number>;
  /** Deletes the device's database, every account in it. The layer stays usable for the next sign-in. */
  clearLocalData(): Promise<void>;
  /** Runs a sync pass now and resolves with its result. */
  sync(accountId: string): Promise<PassResult>;
  /** Resolves once no sync pass is running. */
  whenIdle(): Promise<void>;
  /** Stops timers and listeners and closes the database. */
  dispose(): void;
}

const browserTimers: Timers = {
  setTimeout: (callback, ms) => globalThis.setTimeout(callback, ms),
  clearTimeout: (handle) => globalThis.clearTimeout(handle as ReturnType<typeof setTimeout>),
};

const browserOnline: OnlineSource = (listener) => {
  if (typeof window === "undefined") return () => {};
  window.addEventListener("online", listener);
  return () => window.removeEventListener("online", listener);
};

/** Two tabs of one device sync one at a time, where the browser has Web Locks. */
function browserLock<T>(work: () => Promise<T>): Promise<T> {
  const locks = typeof navigator === "undefined" ? undefined : navigator.locks;
  return locks ? (locks.request("poweryourflow-sync", work) as Promise<T>) : work();
}

export function createDataLayer(options: DataLayerOptions): DataLayer {
  const factory = options.indexedDB ?? globalThis.indexedDB;
  const name = options.dbName ?? DB_NAME;
  const timers = options.timers ?? browserTimers;
  const now = options.now ?? Date.now;
  const questionnaireFor = options.questionnaireFor ?? libQuestionnaireFor;

  let opening: Promise<IDBDatabase> | null = null;
  const db = (): Promise<IDBDatabase> => {
    opening ??= openDatabase(factory, name).catch((error: unknown) => {
      opening = null;
      throw error;
    });
    return opening;
  };
  const close = async (): Promise<void> => {
    const pending = opening;
    opening = null;
    if (pending) (await pending.catch(() => null))?.close();
  };

  const syncer = new Syncer({
    db,
    transport: options.transport,
    timers,
    online: options.online ?? browserOnline,
    lock: options.lock ?? browserLock,
    now,
    ...(options.batch !== undefined ? { batch: options.batch } : {}),
  });
  const deps = { db, questionnaireFor, now };

  /** The Dados gerais save in flight, which a hub read must not miss. */
  let profileSaving: Promise<Profile> | null = null;

  /** `work`'s value, or null when it fails or `ms` pass first. */
  const settleWithin = <T>(work: Promise<T>, ms: number): Promise<T | null> =>
    new Promise((resolve) => {
      const timer = timers.setTimeout(() => resolve(null), ms);
      work.then(resolve, () => resolve(null)).finally(() => timers.clearTimeout(timer));
    });

  /** Runs a local write, then wakes the syncer without waiting on it. */
  const queued = async (accountId: string, write: Promise<AssessmentRun>): Promise<AssessmentRun> => {
    const run = await write;
    syncer.kick(accountId);
    return run;
  };

  return {
    async recordAnswer(accountId, assessmentId, questionId, value, fields) {
      const started = performance.now();
      const run = await queued(accountId, outbox.recordAnswer(deps, accountId, assessmentId, questionId, value, fields));
      const elapsed = performance.now() - started;
      if (elapsed > ANSWER_WRITE_BUDGET_MS) {
        console.warn(`[data] answer write took ${Math.round(elapsed)} ms, budget ${ANSWER_WRITE_BUDGET_MS} ms`, { assessmentId });
      }
      return run;
    },

    recordFinish: (accountId, assessmentId) => queued(accountId, outbox.recordFinish(deps, accountId, assessmentId)),

    requestRedo: (accountId, assessmentId) => queued(accountId, outbox.requestRedo(deps, accountId, assessmentId)),

    saveProfile(fields) {
      const saving = (async () => {
        // The server names the account from the caller's claim; the device
        // stores the profile under the account the reply carries.
        const { profile } = await options.transport.saveProfile({ profile: fields });
        await inTransaction(await db(), [PROFILES], "readwrite", async (tx) => {
          tx.objectStore(PROFILES).put(profile);
        });
        return profile;
      })();
      profileSaving = saving;
      return saving.finally(() => {
        if (profileSaving === saving) profileSaving = null;
      });
    },

    async loadHub(accountId, onRefresh) {
      const database = await db();
      const local = await readHub(database, accountId);
      syncer.kick(accountId);
      const refreshed = refreshHub(database, options.transport, accountId);
      refreshed.catch((error: unknown) => {
        // Offline is the common case here, and the local state stands.
        console.warn("[data] hub refresh failed; showing the device's state", { accountId, code: errorCode(error) });
      });

      // Leaving a Dados gerais edit with ← while its save runs opens Perfil
      // on the old profile: the save lands after both reads, so the screen
      // gets the hub again once it is stored.
      const saving = profileSaving;
      if (saving) {
        void Promise.allSettled([refreshed, saving])
          .then(() => readHub(database, accountId))
          .then(onRefresh, (error: unknown) => {
            console.warn("[data] hub not read after the profile save", { accountId, code: errorCode(error) });
          });
      }

      // Nothing on the device (a sign-in on a new phone) would read as a new
      // account until the server answers: the screen keeps its skeletons.
      if (holdsNothing(local)) {
        const first = await settleWithin(refreshed, FIRST_HUB_WAIT_MS);
        if (first) return first;
      }
      refreshed.then(onRefresh, () => {});
      return local;
    },

    pendingAnswers: (accountId) => outbox.pendingAnswers(deps, accountId),

    async prepareLogout(accountId) {
      let timer: unknown = null;
      const timeout = new Promise<void>((resolve) => {
        timer = timers.setTimeout(resolve, LOGOUT_SYNC_TIMEOUT_MS);
      });
      try {
        await Promise.race([syncer.flush(accountId), timeout]);
      } finally {
        timers.clearTimeout(timer);
      }
      return outbox.pendingAnswers(deps, accountId);
    },

    async clearLocalData() {
      syncer.reset();
      await close();
      await deleteDatabase(factory, name);
    },

    sync: (accountId) => syncer.flush(accountId),
    whenIdle: () => syncer.whenIdle(),

    dispose() {
      syncer.dispose();
      void close();
    },
  };
}
